import React, { useState, useEffect } from 'react';
import { X, Check, Loader, Plus, Trash2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createShift, updateShift } from '../../../services/api/callCenterShifts';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import FieldError from '../../message-center/components/FieldError';
import { blockInvalidNumberKeys, sanitizeNumber } from '../../message-center/utils/rangeValidation';
import styles from './CallCenterShiftModal.module.css';

const EMPTY_FORM = {
  shiftDate: new Date().toISOString().split('T')[0],
  staff: [],
};

const CallCenterShiftModal = ({ mode, shift, businessUsers, onClose, onSuccess }) => {
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (mode === 'edit' && shift) {
      setFormData({
        shiftDate: shift.shiftDate || EMPTY_FORM.shiftDate,
        staff: (shift.staff || []).map((s) => ({
          userId: String(s.userId),
          targetValue: s.targetValue != null ? String(s.targetValue) : '',
        })),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [mode, shift]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddStaff = () => {
    setFormData((prev) => ({ ...prev, staff: [...prev.staff, { userId: '', targetValue: '' }] }));
    if (errors.staff) setErrors((prev) => ({ ...prev, staff: '' }));
  };

  const handleStaffChange = (index, field, value) => {
    setFormData((prev) => {
      const staff = [...prev.staff];
      staff[index] = { ...staff[index], [field]: field === 'targetValue' ? sanitizeNumber(value) : value };
      return { ...prev, staff };
    });
    const errorKey = `staff.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleRemoveStaff = (index) => {
    setFormData((prev) => ({ ...prev, staff: prev.staff.filter((_, i) => i !== index) }));
  };

  const getAvailableUsersForRow = (index) => {
    const selectedElsewhere = formData.staff.filter((_, i) => i !== index).map((s) => s.userId);
    return businessUsers.filter((u) => !selectedElsewhere.includes(String(u.id)));
  };

  const totalTarget = formData.staff.reduce((sum, s) => sum + (parseFloat(s.targetValue) || 0), 0);

  const validate = () => {
    const newErrors = {};

    if (!formData.shiftDate) {
      newErrors.shiftDate = t('callCenterShifts.validation.shiftDateRequired');
    }

    if (formData.staff.length === 0) {
      newErrors.staff = t('callCenterShifts.validation.staffRequired');
    }

    formData.staff.forEach((s, index) => {
      if (!s.userId) {
        newErrors[`staff.${index}.userId`] = t('callCenterShifts.validation.staffUserRequired');
      }
      if (s.targetValue === '') {
        newErrors[`staff.${index}.targetValue`] = t('callCenterShifts.validation.targetRequired');
      } else if (isNaN(s.targetValue) || parseFloat(s.targetValue) <= 0) {
        newErrors[`staff.${index}.targetValue`] = t('callCenterShifts.validation.targetPositive');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError(t('callCenterShifts.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        shiftDate: formData.shiftDate,
        staff: formData.staff.map((s) => ({
          userId: parseInt(s.userId, 10),
          targetValue: parseFloat(s.targetValue),
        })),
      };

      if (mode === 'edit') {
        await updateShift(shift.id, payload);
        success(t('callCenterShifts.shiftUpdatedSuccess'));
      } else {
        await createShift(payload);
        success(t('callCenterShifts.shiftCreatedSuccess'));
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving shift:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToSaveShift'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{mode === 'edit' ? t('callCenterShifts.editShift') : t('callCenterShifts.createShift')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="shiftDate">{t('callCenterShifts.shiftDate')} *</label>
              <DatePicker
                value={formData.shiftDate}
                onChange={(d) => handleChange('shiftDate', d)}
                disabled={isSubmitting}
              />
              <FieldError message={errors.shiftDate} />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="totalTarget">{t('callCenterShifts.totalTarget')}</label>
              <input
                type="text"
                id="totalTarget"
                value={formatCurrency(totalTarget)}
                disabled
                className={styles.totalTargetInput}
              />
              <FieldError message="" />
            </div>

            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <div className={styles.staffHeader}>
                <label>{t('callCenterShifts.shiftStaff')}</label>
                <button type="button" onClick={handleAddStaff} className={styles.addStaffButton} disabled={isSubmitting}>
                  <Plus size={16} />
                  {t('callCenterShifts.addStaff')}
                </button>
              </div>
              <FieldError message={errors.staff} />

              {formData.staff.length > 0 && (
                <div className={styles.staffList}>
                  <div className={styles.staffListHeader}>
                    <span>{t('callCenterShifts.staffMember')}</span>
                    <span>{t('callCenterShifts.targetValue')}</span>
                    <span />
                  </div>
                  {formData.staff.map((s, index) => {
                    const availableUsers = getAvailableUsersForRow(index);
                    return (
                      <div className={styles.staffRow} key={index}>
                        <div className={styles.fieldCell}>
                          <Dropdown
                            value={s.userId}
                            onChange={(v) => handleStaffChange(index, 'userId', v != null ? String(v) : '')}
                            options={availableUsers}
                            displayKey="name"
                            valueKey="id"
                            placeholder={t('callCenterShifts.selectUser')}
                            allowClear={false}
                            size="compact"
                            disabled={isSubmitting}
                            className={errors[`staff.${index}.userId`] ? styles.error : ''}
                            searchable
                          />
                          <FieldError message={errors[`staff.${index}.userId`]} floating />
                        </div>
                        <div className={styles.fieldCell}>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder={t('callCenterShifts.placeholders.targetValue')}
                            value={s.targetValue}
                            onKeyDown={blockInvalidNumberKeys}
                            onChange={(e) => handleStaffChange(index, 'targetValue', e.target.value)}
                            className={errors[`staff.${index}.targetValue`] ? styles.error : ''}
                            disabled={isSubmitting}
                          />
                          <FieldError message={errors[`staff.${index}.targetValue`]} floating />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveStaff(index)}
                          className={styles.removeStaffButton}
                          disabled={isSubmitting}
                          title={t('callCenterShifts.removeStaff')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </form>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {mode === 'edit' ? t('common.updating') : t('common.creating')}
              </>
            ) : (
              <>
                <Check size={18} />
                {mode === 'edit' ? t('callCenterShifts.saveShift') : t('callCenterShifts.createShift')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallCenterShiftModal;
